// src/pages/TestPanel.js
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MessageBox from '../components/MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { useAuth } from '../context/AuthContext'

const TestPanel = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [message, setMessage] = useState(null)
  const { user, refreshUser } = useAuth()

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
    localStorage.setItem('lang', lng)
  }

  const logout = () => {
    fetchWithAuth('/auth/logout', { method: 'POST' })
      .finally(() => refreshUser())
  }

  const fieldLabel = (key) => ({
    email: t('EMAIL'),
    meno: t('FIRST_NAME'),
    priezvisko: t('LAST_NAME'),
    telefon: t('PHONE'),
    ulica: t('STREET'),
    mesto: t('CITY'),
    psc: t('ZIP'),
    krajina: t('COUNTRY'),
    iban: 'IBAN',
    swift: 'SWIFT',
    banka: t('BANK'),
    referral_code: t('REFERRAL_CODE'),
    parent_referral_code: t('PARENT_REFERRAL_CODE'),
    password: t('PASSWORD'),
  }[key] || key)

  const localizeMissing = (msg) => {
    if (!msg) return msg
    const idx = msg.indexOf(':')
    if (idx === -1) return msg
    const prefix = msg.slice(0, idx)
    const rest = msg.slice(idx + 1)
    const localized = rest
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(fieldLabel)
      .join(', ')
    return `${prefix}: ${localized}`
  }

  const fromServerMessage = (data) => {
    const raw = data?.message || ''
    if (/^UNKNOWN_MESSAGE$/i.test(raw)) return 'Unknown message'
    if (/Missing data|Chýbajúce údaje/i.test(raw)) return localizeMissing(raw)
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean)
    return lines.length ? lines[lines.length - 1] : raw
  }

  const payload = user

  const requestAdminMfaCode = () => {
    const code = window.prompt('Zadaj 6-miestny MFA kód z autentifikačnej aplikácie')
    if (!code) return null

    return code.trim()
  }

  const runWebAdminScript = async (payload) => {
    const mfaCode = requestAdminMfaCode()
    if (!mfaCode) return null

    const res = await fetchWithAuth('/admin/run-script', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        mfaCode
      })
    })

    const data = await res.json()
    setMessage({
      message: fromServerMessage(data),
      type: res.ok ? 'success' : 'error'
    })
  }

  return (
    <div style={{ padding: 20 }}>
      {message && <MessageBox message={message.message} type={message.type} />}

      <div style={{ marginBottom: 10 }}>
        <button onClick={() => changeLanguage('sk')}>SK</button>
        <button onClick={() => changeLanguage('en')} style={{ marginLeft: 10 }}>EN</button>

        {user && (
          <button
            onClick={() => window.location.href = '/userprofile'}
            style={{ marginLeft: 20 }}
          >
            {t('HOME')}
          </button>
        )}
      </div>

      {/* Admin Section */}
      {payload?.role === 'admin' && (
        <>
          <h4>{t('ADMIN_SECTION')}</h4>

          {/* Tieto 3 majú byť hore v jednom riadku */}
          <button onClick={() => window.location.href = '/admin/membership-types'}>{t('MEMBERSHIPS')}</button>
          <button onClick={() => window.location.href = '/admin/mlm-bonus-levels'} style={{ marginLeft: 10 }}>{t('MLM_BONUSES')}</button>
          <button onClick={() => window.location.href = '/admin/insurance'} style={{ marginLeft: 10 }}>Poisťovne</button>
          <button onClick={() => window.location.href = '/admin/promo-banners'} style={{ marginLeft: 10 }}>Promobanery</button>

          <div style={{ marginTop: 8 }}>
            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000' }}
              title="Spustí kontrolu termínov STK a EK a odošle upozornenia"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'stk-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              TK/EK reminders
            </button>

            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000', marginLeft: 10 }}
              title="Odošle upozornenie na termín prezúvania pneu (zimné/letné)"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'tirechange-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              Tire change reminder
            </button>

            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000', marginLeft: 10 }}
              title="Odošle upozornenie na termín servisnej prehliadky"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'service-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              Service check reminder
            </button>

            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000', marginLeft: 10 }}
              title="Odošle upozornenie na termín PZP, alebo havarijnej poistky."
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'insurance-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              Insurance reminder
            </button>
          </div>

          <div style={{ marginTop: 8 }}>
            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000', marginLeft: 10 }}
              title="Odošle upozornenie na termín konca predplatného"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'subscription-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              Subscription reminder
            </button>

            <button
              style={{ backgroundColor: '#f4c2d7', color: '#000', marginLeft: 10 }}
              title="Upozornenie na vymazanie účtu po nezakúpení predplatného 30 dní od registrácie"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ script: 'userdelete-reminders' })
                })
                const data = await res.json()
                setMessage({ message: fromServerMessage(data), type: res.ok ? 'success' : 'error' })
              }}
            >
              Account delete reminder
            </button>

            <button
              style={{ backgroundColor: '#b93a3a', color: '#fff', marginLeft: 10 }}
              title="Deaktivuje členov, ktorý si po registrácii do 30 dní nekúpili predplatné"
              onClick={async () => {
                await runWebAdminScript({ script: 'deactivateRegisteredUsers' })
              }}
            >
              Deactivate registered users
            </button>

            <button
              style={{ backgroundColor: '#b93a3a', color: '#fff', marginLeft: 10 }}
              title="Deaktivuje členov, ktorý si 30 dní po expirácii neobnovili predplatné"
              onClick={async () => {
                await runWebAdminScript({ script: 'deactivateUsers' })
              }}
            >
              Deactivate expired users
            </button>

            <button
              style={{ backgroundColor: '#b93a3a', color: '#fff', marginLeft: 10 }}
              title="Reorganizuje sieť po vymazaní deaktivovaných členov"
              onClick={async () => {
                await runWebAdminScript({ script: 'networkRebalance' })
              }}
            >
              Rebalance network
            </button>

            <button
              style={{ backgroundColor: '#b93a3a', color: '#fff', marginLeft: 10 }}
              title="Obnoví sieť k stavu pred poslednou reorganizáciou"
              onClick={async () => {
                await runWebAdminScript({ script: 'networkRebalanceRevert' })
              }}
            >
              Revert rebalance
            </button>
          </div>

          {/* LOAD USERS – presne tam kde to bolo pôvodne, samostatne */}
          <h4> </h4>
          <button onClick={() => navigate('/admin/users')}>
            {t('LOAD_USERS')}
          </button>

          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate('/admin/ledger')}>Účtovná kniha</button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#b93a3a', color: '#fff' }}
              onClick={async () => {
                await runWebAdminScript({ script: 'paymentAllocations' })
              }}
            >
              Backfill Payment Allocations
            </button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#b93a3a', color: '#fff' }}
              onClick={async () => {
                const month = prompt('Zadaj mesiac vo formáte YYYY-MM', new Date().toISOString().slice(0, 7))
                if (!month) return
                await runWebAdminScript({ script: 'cashback', month })
              }}
            >
              Calculate Cashback
            </button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#b93a3a', color: '#fff' }}
              onClick={async () => {
                await runWebAdminScript({ script: 'referrals' })
              }}
            >
              Backfill Referrals
            </button>
          </div>

          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate('/admin/receipts')}>{t('RECEIPTS')}</button>
          </div>

          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate('/admin/wallet-entries')}>Wallet záznamy</button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#f4c2d7', color: '#000' }}
              title="Vytvorí JSON pre výbery a odošle ich do banky"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ script: 'exportPayouts' })
                })
                if (res.ok) {
                  setMessage({ message: 'Exported', type: 'success' })
                } else {
                  const data = await res.json()
                  setMessage({ message: fromServerMessage(data), type: 'error' })
                }
              }}
            >
              Export payouts
            </button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#f4c2d7', color: '#000' }}
              title="Vytvorí JSON, ako keby odpoveď z banky na odoslané výbery"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ script: 'simulateBank' })
                })
                if (res.ok) {
                  setMessage({ message: 'Bank responded', type: 'success' })
                } else {
                  const data = await res.json()
                  setMessage({ message: fromServerMessage(data), type: 'error' })
                }
              }}
            >
              Simulate bank response
            </button>

            <button
              style={{ marginLeft: 10, backgroundColor: '#b93a3a', color: '#fff' }}
              title="Simuluje spracovanie výberov v banke"
              onClick={async () => {
                const res = await fetchWithAuth('/admin/run-script', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ script: 'simulateWebhook' })
                })
                if (res.ok) {
                  setMessage({ message: 'Webhook completed', type: 'success' })
                } else {
                  const data = await res.json()
                  setMessage({ message: fromServerMessage(data), type: 'error' })
                }
              }}
            >
              Webhook
            </button>
          </div>

          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate('/admin/payment-allocations')}>Účtovné alokácie</button>
          </div>

          <button onClick={() => navigate('/admin/cashback-lab')} style={{ marginTop: 10 }}>Cashback Lab</button>

          <div style={{ marginTop: 10 }}>
            <button onClick={() => navigate('/admin/network-viz')}>{t('NETWORKVIZ')}</button>
          </div>

	<div style={{ marginTop: 8 }}>
  	<button onClick={() => navigate('/admin/audit-logs')}>
  	  Audit Logy
 	 </button>
	</div>

	<div style={{ marginTop: 8 }}>
	  <button
 	   onClick={() => window.open(
 	     'https://analytics.enerqiq.net/',
 	     '_blank',
 	     'noopener,noreferrer'
 	   )}
 	 >
 	   Matomo
	  </button>
	</div>
        </>
      )}

      {user && (
        <div style={{ marginTop: 20 }}>
          <button onClick={logout}>{t('LOGOUT')}</button>
        </div>
      )}
    </div>
  )
}

export default TestPanel